import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {inject, Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot} from '@angular/router';
import {HttpClient} from '@angular/common/http';
import {AceLoggerService} from '@logging/ace-logger.service';
import {AceLogger} from '@logging/ace-logger';
import {ClassTransformer} from 'class-transformer';
import * as gql from 'gql-query-builder';
import {BaseService} from './base-service';
import {PagedModel} from '../../api/model/paged-model';
import {Equipment, EquipmentId, MetadataType, MetadataTypeId} from '@app/graphql-typings/graphql';
import {GRAPHQL_TYPES, HttpUtilityService} from '@shared/services/api/http-utility-service';
import {APIResponse} from '@app/layout/admin/services/api/api.interface';
import {StringUtils} from '@shared/util/general/string-utils';
import {TemplateId} from '@app/layout/rules/template-editor/api/template';
import {SetUtils} from '@app/shared/util/collections/collection-utils';

export enum MAINTAINERS {
  KONE = 'Kone',
  SCHINDLER = 'Schindler',
  GWA = 'GWA'
}

export interface MetadataTypeSearchTerm {
  commissionable?: boolean;
  system?: boolean;
  type_ids?: number[];
  shared?: boolean;
  type?: string;
  type_code?: string;
}

class MetadataTypeImpl implements MetadataType {
  commissionable?: boolean;
  system?: boolean;
  type_id: number;
  type: string;
  type_code: string;
  shared: boolean;
  customer_id: number;
}

export const metadataTypeResolver: ResolveFn<MetadataType> = (route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<MetadataType> => {
  return inject(MetadataTypeService).getMetadataType(StringUtils.stringToNumber(route.paramMap.get('id')));
};

@Injectable()
export class MetadataTypeService extends BaseService {

  public static readonly modelMapping = {MetadataType: MetadataTypeImpl};

  private readonly DATA_KEY = 'metadata_type';
  private readonly DATA_KEY_PLURAL = this.DATA_KEY + 's';
  private readonly URL_PART = '/' + this.DATA_KEY_PLURAL;
  private readonly REQUEST_PAGE_SIZE = 2000;
  private readonly PARTITION_SIZE = 1000;
  private readonly logger: AceLogger;

  readonly ELEVATOR_METADATA_TYPE_CODE = 'ELVTR';
  readonly BATHROOM_FIXTURES_TYPE_CODE = 'BFX';
  readonly maintainersByTypeCode: ReadonlyMap<string, ReadonlyArray<MAINTAINERS>> = new Map([
    [this.ELEVATOR_METADATA_TYPE_CODE, [MAINTAINERS.KONE, MAINTAINERS.SCHINDLER]],
    [this.BATHROOM_FIXTURES_TYPE_CODE, [MAINTAINERS.GWA]]
  ]);

  constructor(private http: HttpClient,
              private httpUtilityService: HttpUtilityService,
              private classTransformer: ClassTransformer,
              logService: AceLoggerService) {
    super();
    this.logger = logService.create(MetadataTypeService.name);
  }

  filterMetadataTypes(metadataTypeById: Map<MetadataTypeId, MetadataType>,
                      equipmentById: Map<EquipmentId, Equipment>,
                      metadataTypeIdsByTemplateId: Map<TemplateId, Set<MetadataTypeId>>,
                      templateIds: TemplateId[],
                      equipmentIds: EquipmentId[]): Map<MetadataTypeId, MetadataType> {
    let metadataTypeOptionById: Map<MetadataTypeId, MetadataType>;
    if (metadataTypeById != null && (equipmentById != null || !equipmentIds?.length) && (metadataTypeIdsByTemplateId != null || !templateIds?.length)) {
      if (equipmentIds?.length || templateIds?.length) {
        let equipmentMetadataTypeIds: Set<MetadataTypeId>;
        if (equipmentIds?.length) {
          equipmentMetadataTypeIds = new Set();
          equipmentIds.forEach(equipmentId => {
            const equipment = equipmentById.get(equipmentId);
            if (equipment) {
              equipmentMetadataTypeIds.add(equipment.metadata_type_id);
            }
          });
        }
        let templateMetadataTypeIds: Set<MetadataTypeId>;
        if (templateIds?.length) {
          templateIds.forEach(templateId => {
            const metadataTypeIdSet: Set<MetadataTypeId> = metadataTypeIdsByTemplateId.get(templateId);
            if (metadataTypeIdSet) {
              if (templateMetadataTypeIds == null) {
                templateMetadataTypeIds = metadataTypeIdSet;
              } else {
                templateMetadataTypeIds = SetUtils.union(templateMetadataTypeIds, metadataTypeIdSet);
              }
            }
          });
          templateMetadataTypeIds ??= new Set();
        }
        const metadataTypeIds: Set<MetadataTypeId> = equipmentIds?.length && templateIds?.length
          ? SetUtils.intersection(templateMetadataTypeIds, equipmentMetadataTypeIds)
          : templateMetadataTypeIds ?? equipmentMetadataTypeIds;
        metadataTypeOptionById = new Map();
        metadataTypeIds.forEach(metadataTypeId => {
          const metadataType = metadataTypeById.get(metadataTypeId);
          if (metadataType) {
            metadataTypeOptionById.set(metadataType.type_id, metadataType);
          }
        });
      } else {
        metadataTypeOptionById = new Map(metadataTypeById);
      }
      return metadataTypeOptionById;
    } else {
      return null;
    }
  }

  getMetadataType(id: number): Observable<MetadataType> {
    return this.getMetadataTypesForIds([id]).pipe(map(metadataTypes => metadataTypes?.[0] ?? null));
  }

  getMetadataTypesForIds(ids: number[]): Observable<MetadataType[]> {
    return this.httpUtilityService.requestPartitioned(
      (partitionedIds) =>
        this.getMetadataTypes({first: 0, rows: partitionedIds.length}, {commissionable: null, type_ids: partitionedIds})
          .pipe(map(response => response[this.DATA_PREFIX])),
      ids,
      this.PARTITION_SIZE
    );
  }

  getAllMetadataTypes(searchTerm?: MetadataTypeSearchTerm): Observable<MetadataType[]> {
    return this.httpUtilityService.pageUntilComplete((pageInfo: { first: number, rows: number }) =>
        this.getMetadataTypes(pageInfo, searchTerm), {first: 0, rows: this.REQUEST_PAGE_SIZE});
  }


  getMetadataTypes(pageInfo: { first: number, rows: number },
                   fields?: MetadataTypeSearchTerm): Observable<PagedModel<MetadataType[]>> {
    const query = gql.query({
      operation: 'metadata_types',
      fields: ['type_id', 'type', 'type_code', 'shared', 'customer_id', 'system'],
      variables: {
        type_ids : {type: GRAPHQL_TYPES.INTEGER, list: true},
        type : {type: GRAPHQL_TYPES.STRING},
        type_code : {type: GRAPHQL_TYPES.STRING},
        shared : {type: GRAPHQL_TYPES.BOOLEAN},
        commissionable: {type: GRAPHQL_TYPES.BOOLEAN, value: true},
        system : {type: GRAPHQL_TYPES.BOOLEAN},
        start_index : {type: GRAPHQL_TYPES.INTEGER, required: true},
        limit : {type: GRAPHQL_TYPES.INTEGER, required: true}
      }
    });

    return this.httpUtilityService.graphQL<MetadataType[]>(query, this.DATA_KEY_PLURAL, Object.assign({start_index: pageInfo.first, limit: pageInfo.rows}, fields))
      .pipe(map(result => {
        const modelType = MetadataTypeService.modelMapping['MetadataType'];
        return {
          data: this.classTransformer.plainToInstance(modelType, result.data),
          count: result.count
        };
      }));
  }

  createMetadataType(metadataType: MetadataType): Observable<MetadataType> {
    const options = {headers: this.getHeaders()};
    return this.http.post(this.getGateway() + this.URL_PART, metadataType, options).pipe(map(response => {
      return this.classTransformer.plainToInstance(MetadataTypeImpl, this.httpUtilityService.extractResponseData(response, this.DATA_KEY));
    }));
  }

  updateMetadataType(metadataType: MetadataType): Observable<MetadataType> {
    const options = {headers: this.getHeaders()};
    return this.http.put(this.getGateway() + this.URL_PART + '/' + metadataType.type_id, metadataType, options)
      .pipe(map((response: APIResponse<MetadataType>) =>
        this.classTransformer.plainToInstance(MetadataTypeImpl, this.httpUtilityService.extractResponseData(response, this.DATA_KEY))));
  }

  changeSharingMetadataType(metadataType: MetadataType): Observable<MetadataType> {
    metadataType.shared = !metadataType.shared;
    return this.updateMetadataType(metadataType);
  }

  deleteMetadataType(id: number): Observable<number> {
    const options = {headers: this.getHeaders()};
    return this.http.delete(this.getGateway() + this.URL_PART + '/' + id, options).pipe(map(response => {
      return this.httpUtilityService.extractResponseData(response, this.DATA_KEY)['type_id'];
    }));
  }
}
